import React, { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { NButton, NLink } from '@innersource-nn/novoui-react';
import useDomain from '../hooks/useDomain';
import FormSnackBar from 'components/message/FormSnackBar';

type Props = {
    environmentUri: string;
    domainExists: boolean;
};
interface DomainConfigJsonValidation {
    VpcId: string;
    SubnetIds: string[];
    KmsKeyId: string;
}

export default function EmptyDomain({ environmentUri, domainExists }: Props) {
    const [configFile, setConfigFile] = useState<File>();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { createDomain } = useDomain(environmentUri);

    const handleFileConfigChange = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage('');
        setLoading(false);
        const configFile = event.target.files?.[0];
        setConfigFile(configFile);
    };

    const validateDomainParams = (
        jsonData: DomainConfigJsonValidation,
    ): boolean => {
        let isValid = true;

        if (!jsonData.VpcId) {
            isValid = false;
            setErrorMessage(
                `JSON data validation failed: VpcId not provided in domain configuration file, please add the VPC ID.`,
            );
        }
        if (
            !Array.isArray(jsonData.SubnetIds) ||
            jsonData.SubnetIds.length === 0
        ) {
            isValid = false;
            setErrorMessage(
                `JSON data validation failed: SubnetIds not provided or empty in domain configuration file, please add the private subnet IDs.`,
            );
        }
        if (!jsonData.KmsKeyId) {
            isValid = false;
            setErrorMessage(
                `JSON data validation failed: KmsKeyId not provided in domain configuration file, this key cannot be changed after domain creation, please add the KMS key ARN.`,
            );
        }

        return isValid;
    };
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        if (configFile) {
            const fileReader = new FileReader();
            fileReader.readAsText(configFile, 'UTF-8');

            fileReader.onload = async (readerEvent) => {
                try {
                    // Parse the JSON content
                    const jsonData: DomainConfigJsonValidation = JSON.parse(
                        readerEvent?.target?.result as string,
                    );
                    // Validate the JSON params
                    if (validateDomainParams(jsonData)) {
                        await createDomain(readerEvent);
                    }
                } catch (error: any) {
                    setErrorMessage('Invalid JSON: ' + error.message);
                    setLoading(false);
                }
            };
        }
    };

    return (
        <div className="px-44 py-32">
            <div>
                <form onSubmit={handleSubmit}>
                    <div className="pb-16 text-base text-true-blue">
                        <h1 className="text-lg my-8 font-bold">
                            Create Domain
                        </h1>
                        <p>
                            Make sure you have VPC setup before you create a
                            domain. Please take a look at our{' '}
                            <NLink
                                href="https://docs.datahub.data.novonordisk.cloud/User%20documentation/programmatic-access/#sagemaker"
                                className="underline"
                            >
                                SageMaker documentation
                            </NLink>{' '}
                            for more information about VPC.
                        </p>
                    </div>
                    <NButton
                        className="w-full relative border-true-blue border-dashed font-normal"
                        size="large"
                        variant="outline"
                        square
                        leftIcon="upload"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {configFile?.name || 'Select Domain Configuration file'}
                    </NButton>
                    <input
                        ref={fileInputRef}
                        onChange={handleFileConfigChange}
                        required={!configFile?.name}
                        className="hidden"
                        type="file"
                        accept=".json"
                    />
                    {errorMessage && (
                        <FormSnackBar title={errorMessage} iconSize={40} />
                    )}
                </form>
                <NButton
                    square
                    className="w-full mt-16"
                    variant="fill"
                    onClick={handleSubmit}
                    disabled={!configFile || errorMessage !== ''}
                    isLoading={loading && errorMessage === ''}
                >
                    {domainExists ? 'Update' : 'Create'}
                </NButton>
            </div>
        </div>
    );
}
